import { Link } from 'react-router-dom';
import Navbar2 from '@/component/navbar2';
import Footer from '@/components/BrandFooter';
import { BRAND } from '@/brand';
import { L7_MEDIA } from '@/data/landingMedia';
import {
  PhoneCall, FileSignature, Link2, CalendarCheck, Landmark, TrendingUp,
  ClipboardList, Users, Rocket, PackageCheck, ArrowRight, Check, Minus,
  type LucideIcon,
} from 'lucide-react';
import '@/styles/landingL7.css';
import L7Reveal from '@/components/landing/L7Reveal';

/* How It Works — one route, both brands.
   ---------------------------------------------------------------------------
   `/` differs per domain, so this does too: connectcorporate.com describes the
   setup → books → filings journey, conncct.com describes the readiness → sprint
   → delivery one. Built on the Level 7 primitives so it matches the new landing
   design rather than the older Montserrat pages.

   The nav's "How it works" used to point at `/#how`, which meant leaving whatever
   page you were on, loading the homepage and jumping to a section. It points here
   now. */

const IS_CORP = BRAND === 'corporate';

type Stage = {
  icon: LucideIcon;
  title: string;
  duration: string;
  body: string;
  /** Concrete deliverables — the "very good detail" this page exists for. */
  detail: string[];
};

const CORP_STAGES: Stage[] = [
  {
    icon: PhoneCall,
    title: 'A free call, and a straight answer',
    duration: '30 minutes',
    body: 'We map what you actually do, where your customers are, and which UAE obligations that triggers — before anyone pays anything.',
    detail: [
      'Whether you need a mainland or free-zone entity, and which free zone fits',
      'Whether you cross the VAT threshold (AED 375,000) now or later',
      'Your Corporate Tax position — including whether Small Business Relief applies',
      'A written price for the whole scope, government fees shown separately at cost',
    ],
  },
  {
    icon: FileSignature,
    title: 'Setup, licensing and visas',
    duration: '5–15 working days',
    body: 'If you are starting fresh we form the company; if you already trade, we take the existing entity over and clean up whatever is outstanding.',
    detail: [
      'Trade licence, memorandum and establishment card',
      'Corporate bank account introduction and application support',
      'Residence and employment visas, medical and Emirates ID coordination',
      'FTA registration for VAT and Corporate Tax as required',
    ],
  },
  {
    icon: Link2,
    title: 'Your books, connected',
    duration: '3–5 working days',
    body: 'We wire your income sources into one ledger and configure the tax treatment for each, so nothing has to be re-explained every quarter.',
    detail: [
      'Stripe, marketplaces, payment processors and bank feeds connected',
      'A chart of accounts built for your business, not a generic template',
      'Opening balances migrated and reconciled against your last filed position',
      'Your dashboard live — cash flow, P&L and balance sheet',
    ],
  },
  {
    icon: CalendarCheck,
    title: 'The monthly cycle',
    duration: 'Every month',
    body: 'You send what comes in. We reconcile, flag anything that looks wrong, and keep the ledger closed and current.',
    detail: [
      'Upload invoices and expenses from your phone in seconds',
      'Bookkeeping reconciled to your transaction volume band',
      'Management accounts you can actually read',
      'A named accountant on chat — not a ticket queue',
    ],
  },
  {
    icon: Landmark,
    title: 'Deadlines and filings',
    duration: 'Quarterly and annually',
    body: 'Filings are prepared, shown to you, and submitted by registered FTA tax agents. You approve; we file.',
    detail: [
      'VAT returns prepared and filed quarterly',
      'Corporate Tax return filed annually, with estimates during the year',
      'Reminders before every deadline, not after',
      'If we make an error on a filing we handle, we correct it at no cost',
    ],
  },
  {
    icon: TrendingUp,
    title: 'When you are ready to raise',
    duration: 'Ongoing',
    body: 'Clean books are the difference between a raise that stalls in diligence and one that closes. Everything is already in the shape an investor asks for.',
    detail: [
      'Investor-ready reporting pulled from your live ledger',
      'A capital-readiness score showing exactly what is missing',
      'Data room structure prepared before diligence starts',
    ],
  },
];

const CONS_STAGES: Stage[] = [
  {
    icon: ClipboardList,
    title: 'Start with an honest readiness check',
    duration: '15 minutes',
    body: 'Before anyone sells you anything, you find out where you actually stand against what investors look for.',
    detail: [
      'A scored capital-readiness assessment across governance and readiness dimensions',
      'A written diagnostic showing the specific gaps, not a generic checklist',
      'No signup required to see your result',
    ],
  },
  {
    icon: PackageCheck,
    title: 'Scope the sprint',
    duration: '1–2 days',
    body: 'Work is sold as a fixed scope with a fixed fee and a fixed clock. You know the deliverable and the price before it starts.',
    detail: [
      'One defined problem — fundraising readiness, market entry, diligence prep, GTM',
      'A written scope, a fixed fee and a delivery date',
      'Never a percentage of your raise',
    ],
  },
  {
    icon: Users,
    title: 'Matched to a senior operator',
    duration: '2–5 days',
    body: 'The person doing the work has done it before — raised, advised or exited. No juniors, no pyramid.',
    detail: [
      'Matched on your sector and stage, not on who is free',
      'You meet them before the sprint starts',
      'One operator owns the outcome end to end',
    ],
  },
  {
    icon: Rocket,
    title: 'Delivery you can act on',
    duration: '2–6 weeks',
    body: 'Every sprint ends in a decision-ready output — a model, a plan, a data room, a diligence pack — not a slide deck of observations.',
    detail: [
      'A working artefact you own and can hand to investors',
      'A walkthrough session, so the thinking transfers to your team',
      'Follow-on sprints only if you want them',
    ],
  },
];

const STAGES = IS_CORP ? CORP_STAGES : CONS_STAGES;

/** Who does what — the question every founder actually asks. */
const SPLIT: { you: string[]; us: string[] } = IS_CORP
  ? {
      you: [
        'Tell us what the business does and where the money comes from',
        'Send invoices and expenses as they happen',
        'Approve each filing before it goes to the FTA',
      ],
      us: [
        'Formation, licensing, visas and PRO work',
        'Bookkeeping, reconciliation and management accounts',
        'VAT and Corporate Tax registration, preparation and filing',
        'Deadline tracking, and the reminders that come before them',
        'Investor-ready reporting whenever you need it',
      ],
    }
  : {
      you: [
        'Describe the problem honestly, including what has not worked',
        'Give the operator access to the people and numbers they need',
        'Make the decisions the work is meant to inform',
      ],
      us: [
        'Score where you stand and name the real gaps',
        'Scope the work at a fixed fee with a fixed deadline',
        'Match a senior operator who has solved this before',
        'Deliver an artefact you own, and walk your team through it',
      ],
    };

const FAQ: [string, string][] = IS_CORP
  ? [
      ['Do I have to switch accountants to start?', 'No. We take over an existing entity and reconcile against your last filed position, so nothing is lost in the handover. If a previous filing was wrong, we tell you before we touch anything.'],
      ['Are government fees included in your price?', 'They are passed through at cost and shown as a separate line. We never mark them up, and we never bundle them into a headline figure to make a plan look cheaper.'],
      ['Who actually submits my returns?', 'Registered FTA tax agents. You see the prepared return and approve it; the submission is made on your behalf.'],
      ['What if I am late already?', 'Tell us on the call. Late registration and late filing have specific penalty regimes, and the sooner it is regularised the smaller the exposure. We will be straight with you about what it will cost.'],
    ]
  : [
      ['Is this consulting or a subscription?', 'Neither. Each sprint is a fixed scope with a fixed fee and an end date. There is no retainer and no minimum term.'],
      ['Do you take equity or a percentage of the raise?', 'No. Fees are fixed and published. Nothing we earn is contingent on your round closing, so the advice you get is not shaped by it.'],
      ['Who does the work?', 'A senior operator matched to your sector and stage — someone who has raised, advised or exited. You meet them before the sprint begins.'],
      ['What do I actually receive?', 'A working artefact: a model, a diligence pack, a market-entry plan, a data room. Something you own and can put in front of an investor.'],
    ];

export default function HowItWorks() {
  const primaryCta = IS_CORP ? '/accounting/pricing' : '/capital-raising';
  const primaryLabel = IS_CORP ? 'See pricing' : 'Check your readiness';

  return (
    <div className="l7" data-l7-brand={IS_CORP ? 'corporate' : 'consulting'}>
      <Navbar2 />
      <L7Reveal />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section
        className="l7-sec"
        style={{ background: 'radial-gradient(120% 90% at 82% -10%, var(--acc-soft) 0%, var(--cream) 55%)' }}
      >
        <div className="l7-wrap" style={{ maxWidth: 820, textAlign: 'center' }}>
          <span className="l7-eyebrow">How it works</span>
          <h1 style={{ margin: '20px 0 18px' }}>
            {IS_CORP
              ? <>From first call to filed — <em>step by step</em>.</>
              : <>From where you stand to <em>investor-ready</em>.</>}
          </h1>
          <p className="l7-lead" style={{ margin: '0 auto', maxWidth: 640 }}>
            {IS_CORP
              ? 'No vague onboarding, no surprise invoices. Here is exactly what happens, who does it, how long it takes and what we need from you at each stage.'
              : 'No open-ended retainers and no percentage of your raise. Here is exactly how a sprint runs, who does the work, and what you hold at the end of it.'}
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 28, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="l7-btn l7-btn--acc">Book a free call <ArrowRight size={16} /></Link>
            <Link to={primaryCta} className="l7-btn l7-btn--ghost">{primaryLabel}</Link>
          </div>
        </div>
      </section>

      {/* ── The stages ───────────────────────────────────────── */}
      <section className="l7-sec l7-sec--paper2">
        <div className="l7-wrap">
          <div className="l7-head">
            <span className="l7-eyebrow">The journey</span>
            <h2>{IS_CORP ? <>Six stages. <em>No guesswork.</em></> : <>Four stages. <em>Fixed at every one.</em></>}</h2>
          </div>

          <ol style={{ listStyle: 'none', display: 'grid', gap: 18, margin: 0, padding: 0 }}>
            {STAGES.map(({ icon: Icon, title, duration, body, detail }, i) => (
              <li className="l7-card" key={title} style={{ padding: 'clamp(20px,3vw,30px)' }}>
                <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <span className="l7-ic" style={{ marginBottom: 0, width: 52, height: 52, borderRadius: 14 }}>
                    <Icon size={23} strokeWidth={2} />
                  </span>
                  <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ fontFamily: "'Poppins', system-ui, sans-serif", fontWeight: 800, fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                        Step {i + 1}
                      </span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--acc-d)', background: 'var(--acc-soft)', padding: '4px 10px', borderRadius: 999 }}>
                        {duration}
                      </span>
                    </div>
                    <h3 style={{ fontSize: 'clamp(19px,2.2vw,24px)', marginBottom: 8 }}>{title}</h3>
                    <p style={{ fontSize: 15.5, color: 'var(--ink2)', lineHeight: 1.7 }}>{body}</p>

                    <ul style={{ listStyle: 'none', margin: '16px 0 0', padding: 0, display: 'grid', gap: 9 }}>
                      {detail.map(d => (
                        <li key={d} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14.5, color: 'var(--ink2)' }}>
                          <Check size={17} strokeWidth={2.6} style={{ color: 'var(--green)', flex: 'none', marginTop: 3 }} />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Who does what ────────────────────────────────────── */}
      <section className="l7-sec">
        <div className="l7-wrap">
          <div className="l7-head">
            <span className="l7-eyebrow">Division of labour</span>
            <h2>What you do, and <em>what we handle</em>.</h2>
            <p className="l7-lead">The short version: you make the decisions, we do the paperwork.</p>
          </div>
          <div className="l7-grid l7-grid--2">
            <div className="l7-card" style={{ padding: 26 }}>
              <h3 style={{ fontSize: 19, marginBottom: 14 }}>Your side</h3>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
                {SPLIT.you.map(x => (
                  <li key={x} style={{ display: 'flex', gap: 11, fontSize: 15, color: 'var(--ink2)' }}>
                    <Minus size={17} strokeWidth={3} style={{ color: 'var(--acc-d)', flex: 'none', marginTop: 4 }} />{x}
                  </li>
                ))}
              </ul>
            </div>
            <div className="l7-card" style={{ padding: 26, borderColor: 'var(--ink)', borderWidth: 2 }}>
              <h3 style={{ fontSize: 19, marginBottom: 14 }}>Our side</h3>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
                {SPLIT.us.map(x => (
                  <li key={x} style={{ display: 'flex', gap: 11, fontSize: 15, color: 'var(--ink2)' }}>
                    <Check size={17} strokeWidth={2.6} style={{ color: 'var(--green)', flex: 'none', marginTop: 4 }} />{x}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── UAE band ─────────────────────────────────────────── */}
      <section className="l7-sec l7-sec--tight l7-sec--paper">
        <div className="l7-wrap">
          <div className="l7-figure" style={{ position: 'relative', aspectRatio: '21 / 7' }}>
            <img src={IS_CORP ? L7_MEDIA.dubaiDowntown : L7_MEDIA.dubaiArchitecture} alt="Dubai's business district" loading="lazy" />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(18,47,82,.84) 0%, rgba(18,47,82,.45) 50%, transparent 80%)' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
              <div style={{ padding: 'clamp(20px,4vw,48px)', maxWidth: 560, color: '#fff' }}>
                <h3 style={{ fontSize: 'clamp(19px,2.4vw,27px)', color: '#fff', lineHeight: 1.2 }}>
                  {IS_CORP ? 'Built for the UAE, not adapted to it.' : 'Global operators. Deep GCC execution.'}
                </h3>
                <p style={{ marginTop: 10, fontSize: 15, color: '#D8E1EE' }}>
                  {IS_CORP
                    ? 'Every stage above is shaped by FTA rules and free-zone practice — because that is the only place we work.'
                    : 'Senior operators from global markets, matched to founders building in the Gulf.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section className="l7-sec">
        <div className="l7-wrap" style={{ maxWidth: 860 }}>
          <div className="l7-head">
            <span className="l7-eyebrow">Straight answers</span>
            <h2>The questions we <em>actually get</em>.</h2>
          </div>
          <div style={{ display: 'grid', gap: 14 }}>
            {FAQ.map(([q, a]) => (
              <details key={q} className="l7-card" style={{ padding: '18px 22px' }}>
                <summary style={{ cursor: 'pointer', fontFamily: "'Poppins', system-ui, sans-serif", fontWeight: 700, fontSize: 16 }}>
                  {q}
                </summary>
                <p style={{ marginTop: 10, fontSize: 15, color: 'var(--ink2)', lineHeight: 1.7 }}>{a}</p>
              </details>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: 26, fontSize: 14.5, color: 'var(--muted)' }}>
            More questions? <Link to="/faq" style={{ color: 'var(--acc-d)', fontWeight: 700 }}>Read the full FAQ →</Link>
          </p>
        </div>
      </section>

      {/* ── Close ────────────────────────────────────────────── */}
      <section className="l7-sec l7-sec--navy">
        <div className="l7-wrap" style={{ textAlign: 'center', maxWidth: 700 }}>
          <h2 style={{ color: '#fff' }}>
            {IS_CORP ? <>Start with the call. <em>Then decide.</em></> : <>Start with the score. <em>Then decide.</em></>}
          </h2>
          <p className="l7-lead" style={{ margin: '14px auto 26px' }}>
            {IS_CORP
              ? 'Thirty minutes, a written scope and a price. No obligation to go further.'
              : 'A scored readiness check and a written diagnostic. No signup, no obligation.'}
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="l7-btn l7-btn--acc">Book a free call <ArrowRight size={16} /></Link>
            <Link to={primaryCta} className="l7-btn l7-btn--ghost">{primaryLabel}</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
